import React, { useEffect, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Dumbbell, Users, ListCheck, Award, User, Timer, PersonStanding, Building, ArrowRight } from 'lucide-react';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  className?: string;
  variant?: 'default' | 'accent' | 'dark' | 'cta';
}

function FeatureCard({ icon, title, description, className, variant = 'default' }: FeatureCardProps) {
  const isAccent = variant === 'accent';
  const isDark = variant === 'dark';
  const isCta = variant === 'cta';

  return (
    <div
      className={cn(
        "rounded-[2rem] p-8 border transition-all duration-300 group animated-element h-full flex flex-col",
        isAccent && "bg-accent text-accent-foreground border-transparent",
        isDark && "bg-primary text-primary-foreground border-transparent overflow-hidden relative",
        isCta && "bg-accent text-accent-foreground border-transparent",
        !isAccent && !isDark && !isCta && "bg-card border-border hover:border-accent/30"
      )}
    >
      <div
        className={cn(
          "w-12 h-12 rounded-xl mb-6 flex items-center justify-center transition-all shrink-0",
          isAccent && "bg-white/20 text-white",
          isDark && "bg-white/10 text-accent",
          isCta && "bg-white/20 text-white",
          !isAccent && !isDark && !isCta && "bg-secondary text-accent group-hover:bg-accent group-hover:text-white"
        )}
      >
        {icon}
      </div>
      <h3 className={cn("text-xl font-display font-bold mb-3", (isDark || isAccent || isCta) && "text-primary-foreground")}>
        {title}
      </h3>
      <p className={cn(
        "text-sm leading-relaxed",
        (isAccent || isCta) && "text-accent-foreground/80",
        isDark && "text-primary-foreground/60",
        !isAccent && !isDark && !isCta && "text-muted-foreground"
      )}>
        {description}
      </p>
    </div>
  );
}

export function Features() {
  const { t } = useLanguage();
  const featuresRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.animated-element');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('appear');
              }, i * 100);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (featuresRef.current) {
      observer.observe(featuresRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" className="py-12 md:py-20" ref={featuresRef}>
      <div className="container px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-6">
          {/* Smart Scheduling */}
          <div className="md:col-span-3 lg:col-span-4">
            <FeatureCard
              icon={<Dumbbell className="w-6 h-6" />}
              title={t('features.smartScheduling.title')}
              description={t('features.smartScheduling.description')}
            />
          </div>

          {/* Online Payments */}
          <div className="md:col-span-3 lg:col-span-4">
            <FeatureCard
              icon={<Timer className="w-6 h-6" />}
              title={t('features.onlinePayments.title')}
              description={t('features.onlinePayments.description')}
            />
          </div>

          {/* AI Summaries - dark card */}
          <div className="md:col-span-6 lg:col-span-4">
            <FeatureCard
              icon={<PersonStanding className="w-6 h-6" />}
              title={t('features.aiSummaries.title')}
              description={t('features.aiSummaries.description')}
              variant="dark"
            />
          </div>

          {/* Student Management */}
          <div className="md:col-span-6 lg:col-span-3">
            <FeatureCard
              icon={<Users className="w-6 h-6" />}
              title={t('features.studentManagement.title')}
              description={t('features.studentManagement.description')}
            />
          </div>

          {/* Automatic Invoices */}
          <div className="md:col-span-3 lg:col-span-3">
            <FeatureCard
              icon={<Award className="w-6 h-6" />}
              title={t('features.automaticInvoices.title')}
              description={t('features.automaticInvoices.description')}
            />
          </div>

          {/* School Accounts */}
          <div className="md:col-span-3 lg:col-span-3">
            <FeatureCard
              icon={<Building className="w-6 h-6" />}
              title={t('features.schoolAccounts.title')}
              description={t('features.schoolAccounts.description')}
            />
          </div>

          {/* Parent Accounts */}
          <div className="md:col-span-6 lg:col-span-3">
            <FeatureCard
              icon={<Users className="w-6 h-6" />}
              title={t('features.parentAccounts.title')}
              description={t('features.parentAccounts.description')}
            />
          </div>

          {/* Categories Management */}
          <div className="md:col-span-3 lg:col-span-3">
            <FeatureCard
              icon={<ListCheck className="w-6 h-6" />}
              title={t('features.categoriesManagement.title')}
              description={t('features.categoriesManagement.description')}
            />
          </div>

          {/* Student Accounts */}
          <div className="md:col-span-3 lg:col-span-3">
            <FeatureCard
              icon={<User className="w-6 h-6" />}
              title={t('features.studentAccounts.title')}
              description={t('features.studentAccounts.description')}
            />
          </div>

          {/* CTA card */}
          <div className="md:col-span-6 lg:col-span-6">
            <Link to="/register" className="block h-full">
              <FeatureCard
                icon={<ArrowRight className="w-6 h-6" />}
                title={t('features.title')}
                description={t('features.subtitle')}
                variant="cta"
              />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
